// Keep this policy aligned between the NovaAI frontend and backend.
export function isAllowedAttachmentMimeType(mimeType: string): boolean {
  return (ALLOWED_ATTACHMENT_MIME_TYPES as readonly string[]).includes(mimeType);
}

export const ATTACHMENT_MIME_BY_EXTENSION = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
  gif: 'image/gif',
  pdf: 'application/pdf',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  csv: 'text/csv',
  txt: 'text/plain',
  md: 'text/markdown',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
} as const;

export const ALLOWED_ATTACHMENT_MIME_TYPES = [...new Set(Object.values(ATTACHMENT_MIME_BY_EXTENSION))];
export type AllowedAttachmentMimeType = typeof ALLOWED_ATTACHMENT_MIME_TYPES[number];
export const SUPPORTED_ATTACHMENT_FORMATS = 'PDF, DOCX, XLSX, CSV, TXT, MD, PPTX, PNG, JPG, JPEG, WebP, GIF';
export const CHAT_ATTACHMENT_ACCEPT = [
  ...Object.keys(ATTACHMENT_MIME_BY_EXTENSION).map((extension) => `.${extension}`),
  ...ALLOWED_ATTACHMENT_MIME_TYPES,
].join(',');

/** Browser MIME values vary by OS, especially for Markdown, CSV and Office files. */
export function resolveAttachmentMimeType(file: { name: string; type: string }): AllowedAttachmentMimeType | null {
  const extension = file.name.toLowerCase().split('.').pop() ?? '';
  const mimeType = file.type.toLowerCase().split(';')[0].trim();
  const expected = Object.prototype.hasOwnProperty.call(ATTACHMENT_MIME_BY_EXTENSION, extension)
    ? ATTACHMENT_MIME_BY_EXTENSION[extension as keyof typeof ATTACHMENT_MIME_BY_EXTENSION]
    : undefined;

  if (!expected) {
    // Preserve image/PDF uploads without an extension (e.g. clipboard files).
    if (!file.name.includes('.') && ['image/png', 'image/jpeg', 'image/webp', 'image/gif', 'application/pdf'].includes(mimeType)) {
      return mimeType as AllowedAttachmentMimeType;
    }
    return null;
  }

  if (!mimeType || mimeType === 'application/octet-stream' || mimeType === expected) return expected;
  if (extension === 'csv' && ['application/csv', 'application/vnd.ms-excel', 'text/plain'].includes(mimeType)) return expected;
  if (extension === 'md' && ['text/plain', 'text/x-markdown'].includes(mimeType)) return expected;
  if (['docx', 'xlsx', 'pptx'].includes(extension) && ['application/zip', 'application/x-zip-compressed'].includes(mimeType)) return expected;
  return null;
}
